import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap } from "@/api";
import type { TechLogEntry } from "@/api";
import { Chip } from "@/components/Chip";
import { ErrorNote } from "@/components/ErrorNote";
import { rowFor, useLocalActions } from "@/components/OnThisPc";
import { TechLogCards, severityLabel, severityTone } from "@/components/TechLogCards";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { COMPONENT_JOBS, capitalize, formatDate, formatDateTime, formatHours, formatInProgress, maintenanceLabel } from "@/lib/format";

/** The maintenance page (F431, decision 069): the overview, the systems in the aircraft's chapters with their jobs, the technical log. */
export const Route = createFileRoute("/airframes/$id/maintenance")({
  component: Maintenance,
});

function stateTone(state: string): "ok" | "warn" | "bad" {
  return state === "in_service" ? "ok" : state === "watch" ? "warn" : "bad";
}

function stateLabel(state: string): string {
  return state.replace(/_/g, " ");
}

/** An entry's pill and words. */
function EntryWords({ e }: { e: TechLogEntry }) {
  return (
    <>
      <Chip tone={severityTone(e.severity)}>{severityLabel(e.severity)}</Chip>
      <span>{e.text}</span>
    </>
  );
}

/** The jobs go through the app when the page runs in it (decision 129); in a browser, to the platform. */
function Maintenance() {
  const host = useHost();
  return host ? <MaintenanceUnderHost /> : <MaintenancePlain />;
}

/** A job by the holder (decision 048): the same account as the lease, no companion token; the platform judges the latest state (the aircraft in the hangar). */
function useApiJob(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: string) => unwrap(await api.POST("/api/v1/airframes/{id}/maintenance", { params: { path: { id } }, body: { action } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["maintenance", id] });
      queryClient.invalidateQueries({ queryKey: ["airframe", id] });
    },
  });
}

function MaintenancePlain() {
  const { id } = Route.useParams();
  const job = useApiJob(id);
  return <MaintenanceBody startJob={(action) => job.mutate(action)} jobPending={job.isPending} jobError={job.error} jobNote={null} />;
}

/** In the app, an aircraft held on this PC gets its jobs from the app (decision 129): with X-Plane open they are judged on the parked aircraft, engines off, and said in the cockpit with their countdown. */
function MaintenanceUnderHost() {
  const { id } = Route.useParams();
  const host = useHost()!;
  const status = useQuery(hostQueries.status(host));
  const actions = useLocalActions();
  const job = useApiJob(id);
  const queryClient = useQueryClient();
  const row = rowFor(status.data, id);
  const here = row && row.local?.lease ? row : null;
  if (here) {
    const note = actions.notes[here.uuid];
    return (
      <MaintenanceBody
        startJob={(action) => actions.maintain.mutate({ uuid: here.uuid, action }, { onSuccess: () => queryClient.invalidateQueries({ queryKey: ["maintenance", id] }) })}
        jobPending={actions.maintain.isPending}
        jobError={note && !note.ok ? note.text : null}
        jobNote={note?.ok ? note.text : null}
      />
    );
  }
  return <MaintenanceBody startJob={(action) => job.mutate(action)} jobPending={job.isPending} jobError={job.error} jobNote={null} />;
}

function MaintenanceBody({ startJob, jobPending, jobError, jobNote }: { startJob: (action: string) => void; jobPending: boolean; jobError: unknown; jobNote: string | null }) {
  const { id } = Route.useParams();
  const page = useQuery(queries.maintenance(id));
  const airframe = useQuery(queries.airframe(id));
  const me = useQuery(queries.me());
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<"all" | "open">("all");
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["maintenance", id] });
    queryClient.invalidateQueries({ queryKey: ["airframe", id] });
  };
  const defer = useMutation({
    mutationFn: async (entry: string) => unwrap(await api.POST("/api/v1/airframes/{id}/tech-log/{target}", { params: { path: { id, target: `${entry}:defer` } } })),
    onSuccess: invalidate,
  });
  const acknowledge = useMutation({
    mutationFn: async (entry: string) => unwrap(await api.POST("/api/v1/airframes/{id}/tech-log/{target}", { params: { path: { id, target: `${entry}:acknowledge` } } })),
    onSuccess: invalidate,
  });
  if (page.isError) return <ErrorNote error={page.error} />;
  if (!page.data) return <p className="muted">Loading the maintenance page…</p>;
  const p = page.data;
  const a = airframe.data;
  const lease = a?.lease ?? null;
  const isHolder = !!lease && !!me.data && lease.holder.id === me.data.user.id;
  const manages = a?.your_role === "owner" || a?.your_role === "captain";
  const inProgress = a?.ledger.condition.in_progress ?? null;
  // The faults the aircraft carries (F446), by chapter; a job on the chapter clears them at the next load.
  const faultsOf = (code: string) => p.faults.filter((f) => f.chapter === code);
  const faultState = (state: string) => (state === "scheduled" ? "set on the aircraft at the next load" : state === "active" ? "on the aircraft" : "cleared on the aircraft at the next load");
  return (
    <section className="flex flex-col gap-5" data-testid="maintenance-page">
      <div className="card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-[1.0714rem] font-semibold">Overview</h2>
          <span className="muted text-xs">Returns judged since {p.judged_since}; earlier flights carry no entries.</span>
        </div>
        <dl className="facts" data-testid="maintenance-facts">
          <Fact label="Hours" value={formatHours(p.overview.total_hours)} />
          <Fact label="Cycles" value={String(p.overview.cycles)} />
          <Fact label="Landings judged" value={String(p.overview.landings)} />
          <Fact label="Since the last flight" value={p.overview.days_since_last_flight == null ? "no flight yet" : `${p.overview.days_since_last_flight.toFixed(0)} days`} />
          {p.overview.next_due ? (
            <Fact
              label="Next item"
              value={`${p.overview.next_due.name}: ${
                p.overview.next_due.unit != null && p.overview.next_due.remaining != null
                  ? `${Math.max(0, p.overview.next_due.remaining).toFixed(0)} ${p.overview.next_due.unit}`
                  : [
                      p.overview.next_due.remaining_hours == null ? null : `${Math.max(0, p.overview.next_due.remaining_hours).toFixed(0)} h`,
                      p.overview.next_due.remaining_days == null ? null : `${Math.max(0, p.overview.next_due.remaining_days).toFixed(0)} days`,
                    ]
                      .filter((s) => s !== null)
                      .join(" or ")
              }`}
              tone={stateTone(p.overview.next_due.state)}
            />
          ) : null}
          <Fact
            label="Open"
            value={`${p.overview.open_inspections} inspection${p.overview.open_inspections === 1 ? "" : "s"} due, ${p.overview.open_warnings} warning${p.overview.open_warnings === 1 ? "" : "s"}`}
            tone={p.overview.open_inspections > 0 ? "bad" : p.overview.open_warnings > 0 ? "warn" : "ok"}
          />
          {p.overview.open_faults > 0 ? <Fact label="Faults carried or set at the next load" value={`${p.overview.open_faults} fault${p.overview.open_faults === 1 ? "" : "s"}, cleared by the repair of their system`} tone="bad" /> : null}
          {p.overview.deferred > 0 ? (
            <Fact
              label="Deferred under the MEL"
              value={`${p.overview.deferred} warning${p.overview.deferred === 1 ? "" : "s"}${p.overview.overdue_deferrals > 0 ? `, ${p.overview.overdue_deferrals} past the repair interval` : ""}`}
              tone={p.overview.overdue_deferrals > 0 ? "bad" : "warn"}
            />
          ) : null}
        </dl>
        {p.overview.this_week.length ? (
          <div className="mt-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide muted">This week</h3>
            <ul className="mt-1 flex flex-col gap-1 text-sm">
              {p.overview.this_week.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2">
                  <EntryWords e={e} />
                  {e.pilot ? <span className="muted text-xs">{e.pilot.name}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {inProgress ? (
          <p className="alert alert-ok mt-3 text-sm" data-testid="maintenance-in-progress">
            {capitalize(formatInProgress(inProgress))}. The aircraft is not to be flown meanwhile.
          </p>
        ) : null}
        <ErrorNote error={jobError ?? acknowledge.error ?? defer.error} />
        {jobNote ? (
          <p className="notice text-xs" data-testid="job-note">
            {jobNote}
          </p>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2" data-testid="maintenance-systems">
        {p.systems.map((s) => (
          <div key={s.code} className="card" data-testid={`system-${s.code}`}>
            <h2 className="text-[1.0714rem] font-semibold">
              <span className="font-mono text-xs muted mr-2">{s.code}</span>
              {s.name}
            </h2>
            {faultsOf(s.code).length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1 text-sm" data-testid={`faults-${s.code}`}>
                {faultsOf(s.code).map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center gap-2">
                    <Chip tone={f.state === "clearing" ? "ok" : f.severity === "inspection" ? "bad" : "warn"}>{f.state === "clearing" ? "cleared" : "fault"}</Chip>
                    <span>{f.description}</span>
                    <span className="muted text-xs">
                      {faultState(f.state)}
                      {f.mel ? `, MEL ${f.mel}` : ""}, since {formatDate(f.since)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {s.tracked ? (
              <ul className="mt-2 flex flex-col gap-3">
                {s.components.map((c) => {
                  const jobDef = c.job ? COMPONENT_JOBS.find((j) => j.action === c.job) : undefined;
                  return (
                    <li key={c.name} className="flex flex-col gap-1" data-testid={`component-${c.name}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">{c.label}</span>
                        <span className="flex items-center gap-2">
                          {c.open_entries > 0 ? <span className="muted text-xs">{c.open_entries} open</span> : null}
                          <Chip tone={stateTone(c.state)}>{stateLabel(c.state)}</Chip>
                        </span>
                      </div>
                      <div className="flex flex-wrap items-baseline gap-2 text-sm">
                        <span>
                          {c.counter.toFixed(c.decimals || (c.unit.startsWith("hours") ? 1 : 0))} {c.unit}
                          {c.life != null ? ` of ${c.life.toFixed(0)}` : ""}
                        </span>
                        {c.confidence !== "none" ? <span className="muted text-xs">confidence {c.confidence}</span> : null}
                      </div>
                      {c.used_share != null ? (
                        <div className="h-1.5 w-full overflow-hidden rounded bg-[color:var(--line)]" aria-hidden="true">
                          <div
                            className={`h-full ${c.state === "in_service" ? "bg-[color:var(--ok)]" : c.state === "watch" ? "bg-[color:var(--warn)]" : "bg-[color:var(--bad)]"}`}
                            style={{ width: `${Math.min(100, c.used_share * 100)}%` }}
                          />
                        </div>
                      ) : null}
                      {c.note ? <span className="muted text-xs">{c.note}</span> : null}
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {c.last_job ? (
                          <span className="muted" data-testid={`last-job-${c.name}`}>
                            Last: {maintenanceLabel(c.last_job.action)}
                            {c.last_job.by ? ` by ${c.last_job.by}` : ""}, {formatDateTime(c.last_job.at)}
                          </span>
                        ) : null}
                        {jobDef && isHolder && !inProgress ? (
                          <button type="button" className="btn btn-sm" onClick={() => startJob(jobDef.action)} disabled={jobPending} title={jobDef.hint} data-testid={`job-${jobDef.action}`}>
                            {jobDef.label}, {jobDef.duration}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted mt-2 text-sm">Nothing tracked yet: the aircraft's own recorder files will tell.</p>
            )}
          </div>
        ))}
      </div>
      {!isHolder ? (
        <p className="muted text-xs">
          {lease ? `The jobs are ${lease.holder.name}'s while they have the aircraft.` : "Book the aircraft from the app to run a job on it."}
        </p>
      ) : null}

      <div className="card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-[1.0714rem] font-semibold">Technical log</h2>
          <div className="flex gap-1">
            <button type="button" className={`btn btn-sm ${filter === "all" ? "btn-primary" : ""}`} onClick={() => setFilter("all")}>
              All
            </button>
            <button type="button" className={`btn btn-sm ${filter === "open" ? "btn-primary" : ""}`} onClick={() => setFilter("open")} data-testid="log-open">
              Open
            </button>
          </div>
        </div>
        {p.tech_log.length === 0 ? <p className="muted mt-2 text-sm">Nothing written yet. Every return is read against the aircraft's limits; what a crew would write appears here.</p> : null}
        <div className="mt-3">
          <TechLogCards id={id} entries={p.tech_log} showClosed={filter === "all"} isHolder={isHolder} manages={manages} onAcknowledge={(entry) => acknowledge.mutate(entry)} onDefer={(entry) => defer.mutate(entry)} pending={acknowledge.isPending || defer.isPending} />
        </div>
      </div>

      <p className="muted text-xs">
        The lives: {p.lives.map((l) => `${l.component} ${l.life.toFixed(0)} ${l.unit} (${l.confidence})`).join("; ")}. Corrected when the maintenance planning document is at hand.
      </p>
    </section>
  );
}

function Fact({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "bad" }) {
  return (
    <div className="fact">
      <dt className="muted text-xs">{label}</dt>
      <dd className={`text-sm ${tone === "bad" ? "text-bad" : tone === "warn" ? "text-warn" : ""}`}>{value}</dd>
    </div>
  );
}
